import React, { useState, useEffect } from 'react';

import markdownit from 'markdown-it';
import { createPortal } from 'react-dom';

import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import ErrorIcon from '@atlaskit/icon/core/error';
import InformationIcon from '@atlaskit/icon/core/information';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Link from '@atlaskit/link';
import Lozenge from '@atlaskit/lozenge';
import { Popup } from '@atlaskit/popup';
import { Box, Flex, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { useIntl } from 'src/hooks/intl';
import { useParams } from 'src/router/hooks';

import { messages } from './AiAssistant.i18n';
import { AiAssistantActions } from './AiAssistantActions';
import { LoadingDots } from './LoadingDots';
import { PrismGlowContainer } from './PrismGlowContainer';
import {
  popupContentContainer,
  popupFooterContainer,
  popupFooterFlexContainer,
  popupFooterContent,
  closePortalButtonContainer,
  portalContainer,
  portalContentContainer,
} from './styles';
import { fetchData, getAiUrl, getAiRequestHeaders } from './utils';

type PipelinesAiPrompts = 'DEPLOYMENT_SUMMARY' | 'WHY_DID_MY_BUILD_FAIL';

type Props = {
  vrTestConfig?: {
    loading: boolean;
    error: boolean;
    output: string;
  };
  buttonText: string;
  buttonIcon: React.ReactChild;
  buttonSpacing?: 'default' | 'compact';
  buttonAppearance?: 'default' | 'subtle';
  prompt: PipelinesAiPrompts;
  getContext: () => Promise<string> | string;
  errorMessage: string;
  sentryErrorSegment: string;
  trackAiResultReturned?: (context: string, result: string) => void;
  portalId?: string;
};

const DISCLAIMER_LINK = 'http://www.atlassian.com/trust/atlassian-intelligence';

export const AiAssistant = ({
  vrTestConfig,
  buttonText,
  buttonIcon,
  buttonSpacing = 'default',
  buttonAppearance = 'default',
  prompt,
  getContext,
  errorMessage,
  sentryErrorSegment,
  trackAiResultReturned = () => {},
  portalId,
}: Props): JSX.Element | null => {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(!!vrTestConfig);
  const [loading, setLoading] = useState(vrTestConfig?.loading || false);
  const [error, setError] = useState(vrTestConfig?.error || false);
  const [aiOutput, setAiOutput] = useState(vrTestConfig?.output || '');

  const { repositoryFullSlug } = useParams<{ repositoryFullSlug: string }>();

  const {
    trackAIInteractionInit,
    trackAIResultView,
    trackAIResultError,
    trackAIInteractionDismiss,
  } = useAIEventsInstrumentation();

  useEffect(() => {
    if (isOpen && aiOutput && !vrTestConfig) {
      trackAIResultView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, aiOutput]);

  const fetchAiOutput = async () => {
    setLoading(true);
    setError(false);

    try {
      const context = await getContext();
      const { message } = await fetchData(
        getAiUrl(repositoryFullSlug),
        getAiRequestHeaders(prompt, context),
        sentryErrorSegment
      );
      setAiOutput(message);
      trackAiResultReturned(context, message);
    } catch (e) {
      trackAIResultError({ aiErrorMessage: e.message });
      setError(true);
    }

    setLoading(false);
  };

  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    e?.preventDefault();
    if (isOpen) {
      trackAIInteractionDismiss();
    }
    setIsOpen(!isOpen);

    if (!aiOutput && !loading) {
      trackAIInteractionInit();
      fetchAiOutput();
    }
  };

  const onClose = (e: React.MouseEvent<HTMLElement>) => {
    e?.preventDefault();
    trackAIInteractionDismiss();
    setIsOpen(false);
  };

  const renderLoadingState = () => {
    return (
      <Inline>
        <LoadingDots />
        <Box xcss={xcss({ marginLeft: 'space.050' })}>
          {formatMessage(messages.aiWorking)}
        </Box>
      </Inline>
    );
  };

  const renderErrorState = () => {
    return (
      <Inline space="space.100" alignBlock="center">
        <ErrorIcon color={token('color.icon.danger')} label="" />
        {errorMessage}
        <Button onClick={fetchAiOutput}>
          {formatMessage(messages.tryAgain)}
        </Button>
      </Inline>
    );
  };

  const renderAiOutput = () => {
    if (loading) return renderLoadingState();
    if (error) return renderErrorState();

    const md = markdownit({
      highlight: (str): string => {
        return `
          <pre style="white-space: pre-line; margin: -45px 0 0 0; line-height: normal">
            <code>${md.utils.escapeHtml(str)}</code>
          </pre>`;
      },
    });
    return (
      <>
        <span
          dangerouslySetInnerHTML={{
            __html: md.render(aiOutput),
          }}
        />
        <AiAssistantActions aiOutput={aiOutput} />
      </>
    );
  };

  const renderContentFooter = () => {
    return (
      <Box xcss={popupFooterContainer}>
        <Flex xcss={popupFooterFlexContainer}>
          <Flex xcss={popupFooterContent}>
            <Lozenge>{formatMessage(messages.beta)}</Lozenge>
            <InformationIcon spacing="spacious" label="" />
            <Link href={DISCLAIMER_LINK} appearance="subtle">
              {formatMessage(messages.disclaimer)}
            </Link>
          </Flex>
        </Flex>
      </Box>
    );
  };

  const renderClosePortalButton = () => {
    const label = formatMessage(messages.close);
    return (
      <Box xcss={closePortalButtonContainer}>
        <IconButton
          icon={() => <CrossIcon label={label} />}
          label={label}
          appearance="subtle"
          onClick={onClose}
        />
      </Box>
    );
  };

  const renderPortalContent = () => {
    return (
      <Box xcss={portalContainer}>
        <PrismGlowContainer loading={loading} error={error}>
          <Box xcss={portalContentContainer(loading, error, aiOutput)}>
            {renderAiOutput()}
            {renderClosePortalButton()}
          </Box>
          {renderContentFooter()}
        </PrismGlowContainer>
      </Box>
    );
  };

  const renderPopupContent = () => {
    return (
      <PrismGlowContainer loading={loading} error={error}>
        <Box xcss={popupContentContainer(loading, error)}>
          {renderAiOutput()}
        </Box>
        {renderContentFooter()}
      </PrismGlowContainer>
    );
  };

  const renderPopup = () => {
    return (
      <Popup
        zIndex={600}
        shouldFitViewport
        isOpen={isOpen}
        onClose={onClose}
        placement="auto"
        content={renderPopupContent}
        trigger={triggerProps => (
          <Button
            {...triggerProps}
            appearance={buttonAppearance}
            spacing={buttonSpacing}
            iconBefore={buttonIcon}
            onClick={onClick}
            isSelected={isOpen}
          >
            {buttonText}
          </Button>
        )}
      />
    );
  };

  const renderPortal = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return null;

    return (
      <>
        <Button
          appearance={buttonAppearance}
          spacing={buttonSpacing}
          iconBefore={buttonIcon}
          onClick={onClick}
          isSelected={isOpen}
        >
          {buttonText}
        </Button>
        {createPortal(isOpen && renderPortalContent(), el)}
      </>
    );
  };

  return !portalId ? renderPopup() : renderPortal(portalId);
};
